import React, { MouseEvent, KeyboardEvent, useState, ReactNode } from 'react';
import {
  Dialog,
  IconButton,
  Stack,
  Text,
  Box,
  Inline,
  List,
  ListItem,
  Columns,
  Column,
} from '@origin-digital/ods-core';
import { IconInfo } from '@origin-digital/ods-icons';
import { GreenExplainerNewModal } from './GreenExplainerNewModal';
import { IEFixedAlert } from '../utils/IEFixedAlert';
import { RenderDuring } from '../utils/RenderDuring';
import {
  BLACK_FRIDAY,
  SalesFormEDRPerkAccordionType,
  StaticPerkAccordionType,
} from '../../typings';

const defaultTitle = (
  <Text weight="medium" tone="neutralDark" data-id="rewards-bonus-title-default">
    Origin Everyday Rewards Plan
  </Text>
);

const blackFriday2024Title = (
  <Text weight="medium" tone="neutralDark" data-id="rewards-bonus-title-open">
    Triple your Everyday Rewards points with Black Friday
  </Text>
);

const alwaysOnShortContent = (
  <Text data-id="rewards-bonus-content-default">
    Everyday Rewards member? Add your membership number to earn 5,000 bonus sign up points and earn
    1 point for every $1 (incl GST) bill charge. Origin Go Zero Electricity starts from $1.50 per
    week, and Origin Go Zero Natural Gas starts from $1 per week.
  </Text>
);

const blackFriday2024ShortContent = (
  <Text data-id="rewards-bonus-content-default">
    Everyday Rewards member? Add your membership number and turbo charge your Everyday Rewards
    points this Black Friday. New residential customers can earn 15,000 points for electricity and
    15,000 for natural gas.
  </Text>
);

const alwaysOnContent = (
  <List space="xsmall">
    <ListItem>
      New residential customers can collect up to 10,000 bonus sign-up points, 5,000 for each energy
      type.
    </ListItem>
    <ListItem>
      Renewing your Everyday Rewards Variable plan? Collect a one-off bonus of 2,500 Everyday
      Rewards points for each energy type.
    </ListItem>
    <ListItem>Earn 1 point for every $1 charged for usage and supply (incl. GST).</ListItem>
    <ListItem>Redeem for money off your Woolies shop and more.</ListItem>
    <ListItem>Bonus points for signing up to Origin Go Zero carbon offset add-ons.</ListItem>
  </List>
);

// The EDR content for Black Friday 2024 MUST be rollback to [Always on] as in Figma after the campaign ends
const blackFriday2024Content = (
  <List space="xsmall">
    <ListItem>
      Everyday Rewards member? Add your membership number and turbo charge your Everyday Rewards
      points this Black Friday.
    </ListItem>
    <ListItem>
      New residential customers can earn 15,000 points for electricity and 15,000 for natural gas.
    </ListItem>
    <ListItem>
      Renewing your Everyday Rewards Variable plan? Collect a one-off bonus of 2,500 Everyday
      Rewards points for each energy type.
    </ListItem>
    <ListItem>
      Earn 1 point for every $1 charged for usage and supply (incl. GST). Redeem for money off your
      Woolies shop and more.
    </ListItem>
    <ListItem>Bonus points for signing up to Origin Go Zero carbon offset add-ons.</ListItem>
  </List>
);

const authTitle = (
  <Text weight="medium" tone="neutralDark" data-id="rewards-bonus-title-default">
    Power up your Everyday Rewards points
  </Text>
);
const authAlwaysOnContent = (
  <Text data-id="rewards-bonus-content-default">
    Everyday Rewards member? Add your membership number and power up your Everyday Rewards points.
    Earn 2,500 points for electricity and 2,500 for natural gas.
  </Text>
);

interface PromoBoxProps {
  variant?: 'default' | 'open' | 'auth';
}

export const EverydayRewardsVariablePromoBox = ({ variant = 'default' }: PromoBoxProps) => {
  const [showModal, setShowModal] = useState(false);

  const handleShowModal = (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowModal(true);
  };

  // content for new EDR component in sales-monorepo
  const promoBoxOpenContent = (
    <Stack space="medium" data-id="rewards-bonus-content-open">
      <RenderDuring
        showDefault={alwaysOnContent}
        showDuring={blackFriday2024Content}
        fromDate={BLACK_FRIDAY.startDate}
        endDate={BLACK_FRIDAY.endDate}
      />
      <Inline alignY="center" space="xsmall">
        <Text tone="neutral">More on Everyday Rewards</Text>
        <IconButton
          data-id="everyday-rewards-promo-box-modal-trigger-open"
          noIconPadding={true}
          onClick={handleShowModal}
        >
          <IconInfo size="xsmall" color="grey300" />
        </IconButton>
      </Inline>
    </Stack>
  );

  const moreInfoIconModal = (
    <Box marginLeft="none">
      <Dialog
        id={`promobox-modal`}
        title="Origin Everyday Rewards Plan"
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        {GreenExplainerNewModal}
      </Dialog>
    </Box>
  );

  // content for OLD EDR component in auth renew or sales forms (cross sell)
  const defaultVariantContent = (
    <IEFixedAlert
      icon={false}
      tone="promoteC"
      data-id="everyday-rewards-promo-box"
      data-testid="everyday-rewards-promo-box"
      muiProps={{
        style: {
          maxWidth: '100%',
        },
      }}
    >
      <Stack space="xsmall">
        <Columns space="medium">
          <Column>
            <Stack space="xxsmall">
              {variant == 'default' && (
                <RenderDuring
                  showDefault={defaultTitle}
                  showDuring={blackFriday2024Title}
                  fromDate={BLACK_FRIDAY.startDate}
                  endDate={BLACK_FRIDAY.endDate}
                />
              )}
              {variant == 'auth' && authTitle}
            </Stack>
          </Column>
          <Column width="content" alignX="right">
            <img
              src="https://res.cloudinary.com/originenergy/image/upload/v1606344623/43faa35315f4347eb9962d54418ce63f924a0150.png"
              alt="Everyday Rewards"
              height="44"
            />
          </Column>
        </Columns>
        {variant == 'default' && (
          <RenderDuring
            showDefault={alwaysOnShortContent}
            showDuring={blackFriday2024ShortContent}
            fromDate={BLACK_FRIDAY.startDate}
            endDate={BLACK_FRIDAY.endDate}
          />
        )}
        {variant == 'auth' && authAlwaysOnContent}
        <Columns space="none" collapseBelow="xl">
          <Column width="content">
            <Text>More on&nbsp;</Text>
          </Column>
          <Column width="content">
            <Inline space="xxsmall">
              <Text>Everyday Rewards</Text>
              <IconButton
                data-id="everyday-rewards-promo-box-modal-trigger-default"
                noIconPadding={true}
                onClick={handleShowModal}
              >
                <IconInfo color="grey300" />
              </IconButton>
            </Inline>
          </Column>
        </Columns>
        {showModal && moreInfoIconModal}
      </Stack>
    </IEFixedAlert>
  );

  // content for new EDR sales-monorepo component in compare plans
  const openVariantContent = (
    <Stack
      space="large"
      data-id="everyday-rewards-promo-box-open-variant"
      data-testid="everyday-rewards-promo-box-open-variant"
    >
      {promoBoxOpenContent}
      {showModal && moreInfoIconModal}
    </Stack>
  );

  return <>{variant === 'open' ? openVariantContent : defaultVariantContent}</>;
};

export const EverydayRewardsSalesFormPromoBox = (_edrPoints: string, _fuelType: string) => {
  return (
    <Text data-id="edr-perk-sales-form-body">
      Plus, collect 1 Everyday Rewards point for ever $1 (inc GST) charged for every usage and
      supply
    </Text>
  );
};

/* EDR Perk */
const EDRPerkBody: ReactNode = <EverydayRewardsVariablePromoBox variant="open" />;

const EDRPerkSummary: ReactNode = <Text weight="medium">Collect Everyday Rewards points</Text>;

export const EDRPerkSalesFormSummary = (edrPoints: string, fuelType: string) => {
  return (
    <Text weight="medium" data-id="edr-perk-sales-form-summary">
      {edrPoints} Everyday Rewards points for {fuelType}
    </Text>
  );
};

const EDRPerkIcon =
  'https://res.cloudinary.com/originenergy/image/upload/v1728624760/spot-illustrations/spot_edr-card-simplified.svg';

const EDRPerkAccordionId = 'edr-perk-accordion';

export const EDRPerkContent: StaticPerkAccordionType = {
  accordionSummary: EDRPerkSummary,
  accordionIcon: EDRPerkIcon,
  accordionBody: EDRPerkBody,
  accordionDataId: EDRPerkAccordionId,
};

export const EDRPerkSalesFormContent: SalesFormEDRPerkAccordionType = {
  accordionSummary: EDRPerkSalesFormSummary,
  accordionIcon: EDRPerkIcon,
  accordionBody: EverydayRewardsSalesFormPromoBox,
  accordionDataId: EDRPerkAccordionId,
};
